'use client'

import { clsx } from 'clsx'

import { FC, useState } from 'react'

import { Block } from '@/app/graphql/generated'
import { BtnCallComponent } from '@/app/shared/components/btn-call'
import { ILevel, typeLevels, typeLevelsKids } from '@/app/shared/components/program/constants'
import { LevelInfoComponent } from '@/app/shared/components/program/elements/level-info'
import { LevelListComponent } from '@/app/shared/components/program/elements/level-list'
import { activeTypeLevel, getActiveTypeLevel } from '@/app/shared/components/program/services'
import { IId } from '@/app/shared/interfaces/common.interface'
import { IButton, ISubTitle, ITitle } from '@/app/shared/interfaces/content.interface'
import { clearData } from '@/app/shared/services/data-formatter'
import { ITypeSite } from '@/app/shared/store/layout.store'
import { ButtonComponent } from '@/app/shared/ui/button'
import { ContainerComponent } from '@/app/shared/ui/container'
import { TitleComponent } from '@/app/shared/ui/title'
import { t } from '@/core/lib/locale/i18n'

// interface
interface IProgram extends IId {
  data: Block
  typeSite: ITypeSite
}

interface IProgramQuery {
  title: ITitle
  subTitle: ISubTitle
  button: IButton
}

// component
const ProgramComponent: FC<Readonly<IProgram>> = ({ data, id, typeSite }) => {
  const levelList = data.languageLevels?.map((el) => ({
    name: el?.name,
    id: el?.id,
    is_work: el?.is_work,
    is_tourism: el?.is_tourism,
    is_migration: el?.is_migration,
    status: el?.status,
  })) as ILevel[]

  const [level, setLevel] = useState<number>(0)

  const dataProgram = clearData(JSON.parse(data?.contentAll ?? '')) as IProgramQuery

  const currentInfoLevel = data?.languageLevels && data?.languageLevels[level]

  return (
    <ContainerComponent id={id}>
      <div
        className={
          'mb-[50px] grid w-full items-center justify-stretch gap-5 tablet:mb-10 laptop:mb-[50px] laptop:flex laptop:justify-between laptop:gap-0'
        }
      >
        <div className={'grid gap-[15px]'}>
          <TitleComponent>{dataProgram.title.title}</TitleComponent>
          <span className={'w-full text-[14px] laptop:w-fit desktop:text-base'}>
            {dataProgram.subTitle.subTitle}
          </span>
        </div>
        <div
          className={'flex w-full flex-wrap justify-items-end gap-[15px] laptop:grid laptop:w-fit'}
        >
          <span className={'font-semibold desktop:text-[22px]'}>{t.level()}</span>
          <ul className={'flex flex-wrap items-center gap-[5px] extraMobile:flex-nowrap'}>
            {(typeSite === 'kids' ? typeLevelsKids : typeLevels)?.map((el) => (
              <li key={el.id} className={'w-full laptop:w-fit'}>
                <ButtonComponent
                  variant={activeTypeLevel(el.slug, levelList[level]) ? undefined : 'bordered'}
                  className={clsx(
                    'h-[29px] min-h-0 w-full rounded-medium border-2 border-black px-[15px] text-[14px] laptop:w-fit laptop:text-base',
                    {
                      'bg-content1-700 text-content1-200': activeTypeLevel(
                        el.slug,
                        levelList[level],
                      ),
                    },
                  )}
                  onClick={() => setLevel(getActiveTypeLevel(el?.slug, levelList))}
                  size={'sm'}
                  color={'default'}
                >
                  {el.name}
                </ButtonComponent>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className={'mb-[70px] grid items-start gap-[30px] laptop:grid-cols-[360px_1fr]'}>
        <LevelListComponent activeLevel={level} levels={levelList} callback={(e) => setLevel(e)} />
        <LevelInfoComponent data={currentInfoLevel} index={level} />
      </div>
      <BtnCallComponent className={'w-full mobile:w-[480px]'} text={dataProgram.button.text} />
    </ContainerComponent>
  )
}

export default ProgramComponent
